import type { ReactNode } from 'react';

export function Panel({
  title,
  children,
  className = '',
  actions,
  dense = false,
}: {
  title?: string;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
  dense?: boolean;
}) {
  return (
    <section
      className={`flex min-h-0 flex-col rounded-lg border border-ink-700 bg-ink-850 shadow-panel ${className}`}
    >
      {title && (
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-ink-700 px-3 py-1.5">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">{title}</h2>
          {actions}
        </header>
      )}
      <div className={`min-h-0 flex-1 ${dense ? '' : 'p-3'} overflow-auto`}>{children}</div>
    </section>
  );
}

export function Button({
  children,
  onClick,
  active = false,
  disabled = false,
  title,
  tone = 'default',
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  tone?: 'default' | 'accent' | 'danger';
  className?: string;
}) {
  const base =
    'rounded border px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-40';
  const tones = {
    default: active
      ? 'border-acid-500 bg-acid-900 text-acid-300'
      : 'border-ink-600 bg-ink-800 text-ink-400 hover:border-ink-500 hover:text-white',
    accent: active
      ? 'border-acid-400 bg-acid-500 text-black'
      : 'border-acid-600 bg-acid-900 text-acid-300 hover:bg-acid-600 hover:text-black',
    danger: 'border-red-900 bg-red-950 text-red-300 hover:border-red-700 hover:text-red-100',
  };
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`${base} ${tones[tone]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1">
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">{label}</span>
      {children}
    </label>
  );
}

export function NumberInput({
  value,
  onChange,
  min = 0,
  max = 255,
  width = 'w-16',
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  width?: string;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => {
        const next = Number(e.target.value);
        if (Number.isFinite(next)) onChange(Math.max(min, Math.min(max, next)));
      }}
      className={`${width} rounded border border-ink-600 bg-ink-900 px-2 py-0.5 text-right font-mono text-xs text-white outline-none focus:border-acid-500`}
    />
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  className = '',
  onKeyDown,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      className={`rounded border border-ink-600 bg-ink-900 px-2 py-1 font-mono text-xs text-white outline-none placeholder:text-ink-500 focus:border-acid-500 ${className}`}
    />
  );
}

export function Slider({
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <input
      type="range"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-1 w-32 cursor-pointer appearance-none rounded-full bg-ink-700 accent-acid-500"
    />
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="rounded border border-ink-600 bg-ink-900 px-2 py-0.5 font-mono text-xs text-white outline-none focus:border-acid-500"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-3 py-1 text-left"
    >
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">{label}</span>
      <span
        className={`relative h-4 w-8 shrink-0 rounded-full transition-colors ${
          checked ? 'bg-acid-500' : 'bg-ink-600'
        }`}
      >
        <span
          className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}
